import { showUserFriendlyNotyf, __ } from "../../utils/notyf-helpers.js";

/**
 * @file Shared User Manager Module
 * @description User management and authentication utilities
 */

export class UserManager {
  constructor() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.userPreferences = {};
    this.isInitialized = false;
  }

  /**
   * Initialize user manager
   */
  async init() {
    console.debug("User Manager: Initializing...");

    try {
      await this.loadCurrentUser();
      this.loadUserPreferences();
      this.setupEventListeners();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error("User Manager: Initialization failed:", error);
      return false;
    }
  }

  /**
   * Load current user data
   */
  async loadCurrentUser() {
    try {
      // Check if user is logged in via WordPress
      if (typeof iphubUserData !== "undefined") {
        this.currentUser = iphubUserData;
        this.isAuthenticated = true;
        console.debug("User Manager: User loaded from WordPress data");
      } else {
        // Check localStorage for cached user data
        const cachedUser = this.getCachedUser();
        if (cachedUser) {
          this.currentUser = cachedUser;
          this.isAuthenticated = true;
          console.debug("User Manager: User loaded from cache");
        }
      }
    } catch (error) {
      console.error("User Manager: Error loading user data:", error);
    }
  }

  /**
   * Load user preferences
   */
  loadUserPreferences() {
    try {
      const stored = localStorage.getItem("iphub_user_preferences");
      if (stored) {
        this.userPreferences = JSON.parse(stored);
      }
    } catch (error) {
      console.error("User Manager: Error loading preferences:", error);
      this.userPreferences = {};
    }
  }

  /**
   * Save user preferences
   */
  saveUserPreferences() {
    try {
      localStorage.setItem("iphub_user_preferences", JSON.stringify(this.userPreferences));
    } catch (error) {
      console.error("User Manager: Error saving preferences:", error);
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for login events
    document.addEventListener("user-login", (e) => {
      this.handleUserLogin(e.detail);
    });

    // Listen for logout events
    document.addEventListener("user-logout", () => {
      this.handleUserLogout();
    });

    // Listen for preference changes
    document.addEventListener("user-preference-change", (e) => {
      this.handlePreferenceChange(e.detail);
    });
  }

  /**
   * Handle user login
   */
  handleUserLogin(userData) {
    this.currentUser = userData;
    this.isAuthenticated = true;
    this.cacheUser(userData);

    showUserFriendlyNotyf(__("Welcome back!", "iphub"), "success");

    // Trigger custom event
    const event = new CustomEvent("user-authenticated", {
      detail: { user: userData },
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle user logout
   */
  handleUserLogout() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.clearCachedUser();

    showUserFriendlyNotyf(__("You have been logged out", "iphub"), "info");

    // Trigger custom event
    const event = new CustomEvent("user-logged-out");
    document.dispatchEvent(event);
  }

  /**
   * Handle preference change
   */
  handlePreferenceChange(preferences) {
    this.userPreferences = { ...this.userPreferences, ...preferences };
    this.saveUserPreferences();

    // Trigger custom event
    const event = new CustomEvent("preferences-updated", {
      detail: { preferences: this.userPreferences },
    });
    document.dispatchEvent(event);
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  /**
   * Get user ID
   */
  getUserId() {
    return this.currentUser?.ID || null;
  }

  /**
   * Get user display name
   */
  getUserDisplayName() {
    return this.currentUser?.display_name || __("Guest", "iphub");
  }

  /**
   * Get user email
   */
  getUserEmail() {
    return this.currentUser?.user_email || null;
  }

  /**
   * Get user role
   */
  getUserRole() {
    return this.currentUser?.roles?.[0] || "guest";
  }

  /**
   * Check if user has specific capability
   */
  hasCapability(capability) {
    if (!this.isAuthenticated) return false;

    const userCapabilities = this.currentUser?.allcaps || {};
    return userCapabilities[capability] === true;
  }

  /**
   * Get user preference
   */
  getUserPreference(key, defaultValue = null) {
    return this.userPreferences[key] ?? defaultValue;
  }

  /**
   * Set user preference
   */
  setUserPreference(key, value) {
    this.userPreferences[key] = value;
    this.saveUserPreferences();

    // Trigger preference change event
    const event = new CustomEvent("user-preference-change", {
      detail: { [key]: value },
    });
    document.dispatchEvent(event);
  }

  /**
   * Cache user data
   */
  cacheUser(userData) {
    try {
      localStorage.setItem("iphub_cached_user", JSON.stringify(userData));
    } catch (error) {
      console.error("User Manager: Error caching user:", error);
    }
  }

  /**
   * Get cached user
   */
  getCachedUser() {
    try {
      const cached = localStorage.getItem("iphub_cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error("User Manager: Error getting cached user:", error);
      return null;
    }
  }

  /**
   * Clear cached user
   */
  clearCachedUser() {
    try {
      localStorage.removeItem("iphub_cached_user");
    } catch (error) {
      console.error("User Manager: Error clearing cached user:", error);
    }
  }

  /**
   * Login user
   */
  async loginUser(credentials) {
    try {
      const response = await fetch("/wp-json/jwt-auth/v1/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (data.token) {
        this.handleUserLogin({
          ID: data.user_id,
          user_email: data.user_email,
          display_name: data.user_display_name,
          roles: data.user_roles,
          token: data.token,
        });
        return { success: true, user: this.currentUser };
      } else {
        throw new Error(data.message || "Login failed");
      }
    } catch (error) {
      console.error("User Manager: Login error:", error);
      showUserFriendlyNotyf(__("Login failed. Please try again.", "iphub"), "error");
      return { success: false, error: error.message };
    }
  }

  /**
   * Logout user
   */
  async logoutUser() {
    try {
      // Call WordPress logout endpoint
      await fetch("/wp-json/wp/v2/users/me", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${this.currentUser?.token}`,
        },
      });
    } catch (error) {
      console.error("User Manager: Logout error:", error);
    } finally {
      this.handleUserLogout();
    }
  }

  /**
   * Register new user
   */
  async registerUser(userData) {
    try {
      const response = await fetch("/wp-json/wp/v2/users/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (data.id) {
        showUserFriendlyNotyf(__("Registration successful! Please log in.", "iphub"), "success");
        return { success: true, userId: data.id };
      } else {
        throw new Error(data.message || "Registration failed");
      }
    } catch (error) {
      console.error("User Manager: Registration error:", error);
      showUserFriendlyNotyf(__("Registration failed. Please try again.", "iphub"), "error");
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profileData) {
    if (!this.isAuthenticated) {
      return { success: false, error: "User not authenticated" };
    }

    try {
      const response = await fetch(`/wp-json/wp/v2/users/${this.getUserId()}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.currentUser?.token}`,
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (data.id) {
        this.currentUser = { ...this.currentUser, ...data };
        this.cacheUser(this.currentUser);

        showUserFriendlyNotyf(__("Profile updated successfully!", "iphub"), "success");
        return { success: true, user: this.currentUser };
      } else {
        throw new Error(data.message || "Profile update failed");
      }
    } catch (error) {
      console.error("User Manager: Profile update error:", error);
      showUserFriendlyNotyf(__("Profile update failed. Please try again.", "iphub"), "error");
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user statistics
   */
  getUserStats() {
    return {
      isAuthenticated: this.isAuthenticated,
      userId: this.getUserId(),
      displayName: this.getUserDisplayName(),
      role: this.getUserRole(),
      preferencesCount: Object.keys(this.userPreferences).length,
      isInitialized: this.isInitialized,
    };
  }

  /**
   * Destroy user manager
   */
  destroy() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.userPreferences = {};
    this.isInitialized = false;
  }
}

/**
 * Initialize user manager
 */
export function initUserManager() {
  const userManager = new UserManager();
  return userManager.init().then((success) => {
    if (success) {
      // Store instance globally for access
      window.IPHUB_USER_MANAGER = userManager;
    }
    return userManager;
  });
}
